import { useEffect, useState } from 'react';
import { useDisclosure } from '@mantine/hooks';
import {
  Box,
  Paper,
  Group,
  Title,
  Button,
  TextInput,
  Select,
  Table,
  Badge,
  ActionIcon,
  Menu,
  Text,
  Avatar,
  Stack,
  rem,
  ScrollArea,
  SimpleGrid,
  Modal,
  Alert,
  Textarea,
} from '@mantine/core';
import {
  IconSearch,
  IconPlus,
  IconFilter,
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconFileDescription,
  IconUserShield,
} from '@tabler/icons-react';
import AddEmployeeModal from '../../components/HumanResources/AddEmployeeModal'; 
import EmployeeProfileModal from '../../components/HumanResources/EmployeeProfileModal';
import UploadDocumentModal from '../../components/HumanResources/UploadDocumentModal';
import api from '../../api/axiosConfig';

export default function EmployeesPage() {
  const [search, setSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState(null);
  const [departmentFilter, setDepartmentFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addModalOpened, { open: openAddModal, close: closeAddModal }] = useDisclosure(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [profileModalOpened, { open: openProfileModal, close: closeProfileModal }] = useDisclosure(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [documentEmployee, setDocumentEmployee] = useState(null);
  const [uploadModalOpened, { open: openUploadModal, close: closeUploadModal }] = useDisclosure(false);
  const [deactivateEmployee, setDeactivateEmployee] = useState(null);
  const [exitReason, setExitReason] = useState('');

  const normalizeEmployee = (item) => ({ ...item, tc: item.identity_number, name: `${item.first_name || ''} ${item.last_name || ''}`.trim(), role: item.job_title?.name || item.position || '-', department: item.department || '-', location: item.employee_type === 'SITE' ? (item.site_name || 'Tanımsız Şantiye') : 'Merkez', status: item.is_active ? 'Aktif' : 'İşten Ayrıldı', phone: item.phone || '-', startDate: item.hire_date });

  const fetchEmployees = async () => {
    try {
      setLoading(true); setError('');
      const response = await api.get('/hr/employees');
      setEmployees(response.data.map(normalizeEmployee));
    } catch (err) {
      console.error(err); setError('Personel listesi alınamadı. Backend bağlantısını kontrol edin.');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchEmployees(); }, []);

  const toApiPayload = (data) => {
    const fullName = (data.name || '').trim().split(/\s+/);
    return {
      identity_number: data.identity_number ?? data.tc ?? null,
      employee_no: data.employee_no ?? null,
      first_name: data.first_name ?? fullName[0] ?? '',
      last_name: data.last_name ?? fullName.slice(1).join(' ') ?? '',
      employee_type: data.employee_type ?? (data.location && data.location !== 'Merkez' ? 'SITE' : 'OFFICE'),
      job_title_id: data.job_title_id ? Number(data.job_title_id) : null,
      position: data.position ?? data.role ?? null, phone: data.phone || null, email: data.email || null,
      department: data.department && data.department !== '-' ? data.department : null,
      site_name: (data.employee_type === 'SITE' || (data.location && data.location !== 'Merkez')) ? (data.site_name ?? data.location) : null,
      is_active: data.is_active ?? true, hire_date: data.hire_date ?? data.startDate ?? null,
    };
  };

  // TC Kimlik Maskeleme Fonksiyonu (Örn: 123*****901)
  const maskTC = (tc) => {
    if (!tc || tc.length !== 11) return tc;
    return `${tc.substring(0, 3)}*****${tc.substring(8, 11)}`;
  };

  // Duruma Göre Renk Belirleme
  const getStatusBadge = (status) => {
    switch (status) {
      case 'Aktif': return <Badge color="green" variant="light">Aktif</Badge>;
      case 'İzinde': return <Badge color="orange" variant="light">İzinde</Badge>;
      case 'İşten Ayrıldı': return <Badge color="gray" variant="light">İşten Ayrıldı</Badge>;
      case 'Pasif': return <Badge color="red" variant="light">Pasif</Badge>;
      default: return <Badge color="gray" variant="light">{status}</Badge>;
    }
  };

  // Arama ve Filtreleme Mantığı
  const filteredEmployees = employees.filter((emp) => {
    const query = search.trim().toLocaleLowerCase('tr-TR');
    const matchSearch = 
      (emp.name || '').toLocaleLowerCase('tr-TR').includes(query) || 
      (emp.tc || '').includes(search.trim());
    const matchLocation = locationFilter ? emp.location === locationFilter : true;
    const matchDepartment = departmentFilter ? emp.department === departmentFilter : true;
    const matchStatus = statusFilter ? emp.status === statusFilter : true;
    return matchSearch && matchLocation && matchDepartment && matchStatus;
  });

  const locationOptions = [...new Set(employees.map((item) => item.location))];
  const departmentOptions = [...new Set(employees.map((item) => item.department))];

  return (
    <Box p={{ base: 'md', lg: 'xl' }} w="100%" h="100%">
      <Stack gap="lg">
        {error && <Alert color="red" title="Bağlantı hatası">{error}</Alert>}
        
        {/* ÜST BAŞLIK VE AKSİYON BUTONU */}
        <Group justify="space-between" align="center">
          <div>
            <Title order={2} fw={800} c="dark.8">Personel Yönetimi</Title>
            <Text size="sm" c="dimmed">Şirket bünyesindeki tüm çalışanların detaylı listesi</Text>
          </div>
          <Button 
            leftSection={<IconPlus size={18} />} 
            radius="md" 
            size="md"
            color="blue"
            onClick={() => {
                setEditingEmployee(null); // İçini boşalt ki "Yeni Kayıt" modunda açılsın
                openAddModal();
            }}
          >
            Yeni Personel Ekle
          </Button>
        </Group>

        {/* FİLTRELEME VE ARAMA ÇUBUĞU */}
        <Paper withBorder p="md" radius="md" bg="#fff" style={{ borderColor: '#e2e8f0' }}>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
            <TextInput
              placeholder="İsim veya T.C. Kimlik No ile ara..."
              label="Personel Ara"
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => {const value = e.currentTarget.value;setSearch(e.currentTarget.value)}}
              radius="md"
            />
            <Select
              label="Lokasyon / Şantiye"
              placeholder="Tümü"
              data={locationOptions}
              value={locationFilter}
              onChange={setLocationFilter}
              clearable
              leftSection={<IconFilter size={16} />}
              radius="md"
            />
            <Select label="Departman" placeholder="Tümü" data={departmentOptions} value={departmentFilter} onChange={setDepartmentFilter} clearable radius="md" />
            <Select label="Durum" placeholder="Tümü" data={['Aktif', 'İzinde', 'İşten Ayrıldı', 'Pasif']} value={statusFilter} onChange={setStatusFilter} clearable radius="md" />
          </SimpleGrid>
        </Paper>

        {/* VERİ TABLOSU */}
        <Paper withBorder radius="md" style={{ overflow: 'hidden', borderColor: '#e2e8f0', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
          <ScrollArea>
            <Table stickyHeader verticalSpacing="md" horizontalSpacing="lg" striped highlightOnHover>
              <Table.Thead bg="gray.0">
                <Table.Tr>
                  <Table.Th>Personel</Table.Th>
                  <Table.Th>T.C. Kimlik</Table.Th>
                  <Table.Th>Görev / Unvan</Table.Th>
                  <Table.Th>Departman</Table.Th>
                  <Table.Th>Lokasyon</Table.Th>
                  <Table.Th>Durum</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>İşlemler</Table.Th>
                </Table.Tr>
              </Table.Thead>

              <Table.Tbody>
                {!loading && filteredEmployees.length > 0 ? (
                  filteredEmployees.map((emp) => (
                    <Table.Tr key={emp.id}>
                      {/* Personel İsim ve Avatar */}
                      <Table.Td>
                        <Group gap="sm">
                          <Avatar color="blue" radius="xl" size="md">
                            {emp.name.split(' ').map(n => n[0]).join('')}
                          </Avatar>
                          <div>
                            <Text fz="sm" fw={600}>{emp.name}</Text>
                            <Text fz="xs" c="dimmed">{emp.phone}</Text>
                          </div>
                        </Group>
                      </Table.Td>

                      {/* T.C. Kimlik */}
                      <Table.Td>
                        <Text fz="sm" fw={500} ff="monospace">{maskTC(emp.tc)}</Text>
                      </Table.Td>

                      {/* Görev */}
                      <Table.Td>
                        <Text fz="sm">{emp.role}</Text>
                      </Table.Td>

                      <Table.Td><Text fz="sm">{emp.department}</Text></Table.Td>

                      {/* Lokasyon */}
                      <Table.Td>
                        <Text fz="sm" fw={500}>{emp.location}</Text>
                      </Table.Td>

                      {/* Durum */}
                      <Table.Td>
                        {getStatusBadge(emp.status)}
                      </Table.Td>

                      {/* İşlemler (Dropdown Menü) */}
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Menu shadow="md" width={200} position="bottom-end">
                          <Menu.Target>
                            <ActionIcon variant="subtle" color="gray" radius="md">
                              <IconDotsVertical size={18} />
                            </ActionIcon>
                          </Menu.Target>

                          <Menu.Dropdown>
                            <Menu.Label>İşlemler</Menu.Label>
                            <Menu.Item 
                            leftSection={<IconUserShield style={{ width: rem(14), height: rem(14) }} />}
                            onClick={() => {
                                setSelectedEmployee(emp); // Tıklanan personeli state'e atıyoruz
                                openProfileModal(); // Modalı açıyoruz
                            }}
                            >
                              Profili Görüntüle
                            </Menu.Item>
                            <Menu.Item 
                                leftSection={<IconEdit style={{ width: rem(14), height: rem(14) }} />}
                                onClick={() => {
                                    setEditingEmployee(emp); // Tıklanan veriyi state'e at
                                    openAddModal(); // Ekleme modalını aç (Ama edit modunda açılacak)
                                }}
                            >
                                Düzenle
                            </Menu.Item>
                            <Menu.Item 
                                leftSection={<IconFileDescription style={{ width: rem(14), height: rem(14) }} />}
                                onClick={() => {
                                    setDocumentEmployee(emp);
                                    openUploadModal();
                                }}
                            >
                                Evrak Yükle
                            </Menu.Item>

                            <Menu.Divider />

                            <Menu.Item 
                              color="red" 
                              leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
                              disabled={emp.status === 'İşten Ayrıldı'}
                              onClick={() => setDeactivateEmployee(emp)}
                            >
                              İşten Ayrılış / Pasife Al
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Table.Td>
                    </Table.Tr>
                  ))
                ) : (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <Group justify="center" py="xl">
                        <Text c="dimmed" fs="italic">{loading ? 'Personeller yükleniyor...' : 'Arama kriterlerine uygun personel bulunamadı.'}</Text>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Paper>
      </Stack>
      <AddEmployeeModal 
        opened={addModalOpened} 
        onClose={closeAddModal} 
        editData={editingEmployee}
        onSave={async (employeeData) => {
          try {
            const payload = toApiPayload(employeeData);
            if (editingEmployee) await api.put(`/hr/employees/${editingEmployee.id}`, payload);
            else await api.post('/hr/employees', payload);
            closeAddModal(); await fetchEmployees();
          } catch (err) {
            console.error(err); setError(err.response?.data?.detail || 'Personel kaydedilemedi.');
          }
        }}
      />
      <EmployeeProfileModal 
        opened={profileModalOpened} 
        onClose={closeProfileModal} 
        employee={selectedEmployee} 
      />
      <UploadDocumentModal 
        opened={uploadModalOpened}
        onClose={closeUploadModal}
        employee={documentEmployee}
        onUpload={async (data) => {
          try {
            const formData = data instanceof FormData ? data : new FormData();
            if (!(data instanceof FormData)) {
              if (data.file) formData.append('file', data.file);
              formData.append('document_type_id', data.document_type_id || data.documentTypeId);
              if (data.expires_at || data.expiresAt) formData.append('expires_at', data.expires_at || data.expiresAt);
              if (data.note) formData.append('note', data.note);
            }
            if (!formData.has('employee_id')) formData.append('employee_id', documentEmployee.id);
            await api.post('/hr/documents/upload', formData);
            closeUploadModal();
          } catch (err) { console.error(err); setError(err.response?.data?.detail || 'Evrak yüklenemedi.'); }
        }}
      />
      <Modal opened={Boolean(deactivateEmployee)} onClose={() => setDeactivateEmployee(null)} title="Personel durumunu değiştir" centered>
        <Stack>
          <Text size="sm"><b>{deactivateEmployee?.name}</b> personelini işten ayrıldı olarak işaretlemek üzeresiniz. Kayıt ve geçmiş hareketleri silinmeyecektir.</Text>
          <Textarea label="İşten Ayrılış Nedeni" value={exitReason} onChange={(e) => setExitReason(e.currentTarget.value)} placeholder="İsteğe bağlı açıklama" />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeactivateEmployee(null)}>Vazgeç</Button>
            <Button color="red" onClick={async () => {
              try {
                await api.post(`/hr/employees/${deactivateEmployee.id}/exit`, { exit_date: new Date().toISOString(), exit_reason: exitReason.trim() || null });
                setDeactivateEmployee(null); setExitReason(''); await fetchEmployees();
              } catch (err) { console.error(err); setError(err.response?.data?.detail || 'Personel durumu güncellenemedi.'); }
            }}>İşten Ayrıldı Olarak İşaretle</Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
